/* eslint-disable no-useless-escape */
import React, { useState, useEffect, useCallback } from 'react';
import {
  SafeAreaView,
  ScrollView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Alert,
  Image,
  KeyboardAvoidingView,
  Platform,
  StatusBar,
  ActivityIndicator,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';
import SupplierForm from './components/SupplierForm';

const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  companyProfile: `${API_BASE_URL}/api/data/company_profiles`,
  vendors: `${API_BASE_URL}/api/data/vendors`,          // unified endpoint
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        config.params = { ...config.params, company_id: parseInt(companyId) };
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const EditSupplierScreen = ({ navigation, route }) => {
  const { supplier } = route.params; // supplier object (already transformed by list)
  const [companyLogo, setCompanyLogo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    location: '',
    materials: [],
    status: 'Active',
    notes: '',
    website: '',
    contactPerson: '',
    companySize: '',
    yearsInBusiness: '',
    ratings: { quality: 0, reliability: 0, cost: 0, time: 0 },
  });
  const [errors, setErrors] = useState({});

  useFocusEffect(
    useCallback(() => {
      loadCompanyLogo();
    }, [])
  );

  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setCompanyLogo(profile.logo || null);
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        const response = await api.get(API_ENDPOINTS.companyProfile, {
          params: { company_id: companyId },
        });
        if (response.data?.success && response.data.data?.length > 0) {
          const latestProfile = response.data.data[0];
          setCompanyLogo(latestProfile.logo || null);
          await AsyncStorage.setItem('company_profile', JSON.stringify(latestProfile));
        }
      }
    } catch (error) {
      console.warn('Error loading company logo:', error);
    }
  };

  useEffect(() => {
    if (supplier) {
      setFormData({
        name: supplier.name || '',
        phone: supplier.contact?.phone || '',
        email: supplier.contact?.email || '',
        location: supplier.location || '',
        materials: supplier.materials || [],
        status: supplier.status || 'Active',
        notes: supplier.notes || '',
        website: supplier.contact?.website || '',
        contactPerson: supplier.contact?.person || '',
        companySize: supplier.companySize || '',
        yearsInBusiness: supplier.yearsInBusiness || '',
        ratings: {
          quality: supplier.ratings?.quality || 0,
          reliability: supplier.ratings?.reliability || 0,
          cost: supplier.ratings?.cost || 0,
          time: supplier.ratings?.time || 0,
        },
      });
    }
  }, [supplier]);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) => /^[\+]?[1-9][\d]{0,15}$/.test(phone.replace(/\s/g, ''));

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Supplier name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    else if (!validatePhone(formData.phone)) newErrors.phone = 'Enter a valid phone number';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!validateEmail(formData.email)) newErrors.email = 'Enter a valid email';
    if (!formData.location.trim()) newErrors.location = 'Location is required';
    if (formData.materials.length === 0) newErrors.materials = 'Select at least one material category';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handleRatingChange = (type, value) => {
    setFormData(prev => ({
      ...prev,
      ratings: { ...prev.ratings, [type]: value },
    }));
  };

  const handleToggleMaterial = (material) => {
    setFormData(prev => ({
      ...prev,
      materials: prev.materials.includes(material)
        ? prev.materials.filter(m => m !== material)
        : [...prev.materials, material],
    }));
    if (errors.materials) setErrors(prev => ({ ...prev, materials: null }));
  };

  const calculateOverallRating = () => {
    const { quality, reliability, cost, time } = formData.ratings;
    return ((quality + reliability + cost + time) / 4).toFixed(1);
  };

  const handleSave = async () => {
    if (!validateForm()) {
      Alert.alert('Validation Error', 'Please correct the errors before saving.');
      return;
    }
    setLoading(true);
    try {
      const companyId = await AsyncStorage.getItem('company_id');
      const payload = {
        company_id: parseInt(companyId),
        company_name: formData.name,
        contact_person: formData.contactPerson,
        email: formData.email,
        phone: formData.phone,
        address: formData.location,
        city: formData.location.split(',')[0]?.trim(),
        country: 'United Kingdom',
        website: formData.website,
        company_size: formData.companySize,
        years_in_business: formData.yearsInBusiness,
        materials: formData.materials,
        status: formData.status,
        notes: formData.notes,
        ratings: formData.ratings,
        overall: parseFloat(calculateOverallRating()),
        type: 'supplier', // keep the type
      };
      await api.put(`${API_ENDPOINTS.vendors}/${supplier.id}`, payload);
      Alert.alert('Success', 'Supplier updated successfully!', [
        { text: 'OK', onPress: () => navigation.goBack() }
      ]);
    } catch (error) {
      console.error('Error updating supplier:', error);
      Alert.alert('Error', 'Failed to update supplier. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    Alert.alert(
      'Delete Supplier',
      `Are you sure you want to delete "${supplier.name}"?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            setLoading(true);
            try {
              await api.delete(`${API_ENDPOINTS.vendors}/${supplier.id}`);
              Alert.alert('Deleted', 'Supplier deleted.', [
                { text: 'OK', onPress: () => navigation.goBack() }
              ]);
            } catch (error) {
              Alert.alert('Error', 'Failed to delete supplier.');
            } finally {
              setLoading(false);
            }
          },
        },
      ]
    );
  };

  const handleCancel = () => {
    Alert.alert('Discard Changes', 'Discard your changes?', [
      { text: 'Keep Editing', style: 'cancel' },
      { text: 'Discard', style: 'destructive', onPress: () => navigation.goBack() },
    ]);
  };

  const handleGoBack = handleCancel;

  const renderHeader = () => (
    <View style={styles.header}>
      <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
        <Ionicons name="chevron-back" size={24} color="#fff" />
      </TouchableOpacity>
      <View style={styles.headerCenter}>
        {companyLogo ? (
          <Image source={{ uri: companyLogo || undefined }} style={styles.headerLogo} />
        ) : (
          <MaterialCommunityIcons name="office-building" size={30} color="#fff" />
        )}
        <Text style={styles.headerTitle}>Edit Supplier</Text>
      </View>
      <TouchableOpacity style={styles.saveHeaderButton} onPress={handleSave} disabled={loading}>
        {loading ? <ActivityIndicator size="small" color="#fff" /> : <Text style={styles.saveButtonText}>Save</Text>}
      </TouchableOpacity>
    </View>
  );

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
      {renderHeader()}
      <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
          <SupplierForm
            formData={formData}
            errors={errors}
            onInputChange={handleInputChange}
            onToggleMaterial={handleToggleMaterial}
            onRatingChange={handleRatingChange}
            showRatings={true}
            isLoading={loading}
            onSave={handleSave}
            onCancel={handleCancel}
            onDelete={handleDelete}
            saveButtonText="Update Supplier"
            showDeleteButton={true}
          />
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#4A90E2' },
  header: {
    backgroundColor: '#4A90E2',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: { padding: 5 },
  headerCenter: { flexDirection: 'row', alignItems: 'center' },
  headerLogo: { width: 30, height: 30, borderRadius: 15, resizeMode: 'cover' },
  headerTitle: { color: '#fff', fontSize: 20, fontWeight: 'bold', marginLeft: 10 },
  saveHeaderButton: { padding: 5 },
  saveButtonText: { color: '#fff', fontSize: 16, fontWeight: '600' },
  container: { flex: 1, backgroundColor: '#f8fafc' },
  scrollView: { flex: 1 },
});

export default EditSupplierScreen;